import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, Volume2, ArrowRight, Sparkles, Check } from 'lucide-react';

const HeroSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);

  const togglePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => setCurrentTime(videoRef.current?.currentTime || 0);
  const handleLoadedMetadata = () => setDuration(videoRef.current?.duration || 0);

  const handleSeek = (e) => {
    if (!videoRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = percent * duration;
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <section className="relative text-white bg-gradient-to-b from-gray-950 via-blue-950 to-gray-950 overflow-hidden">
      {/* Decorative background video + soft radial glow */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0 opacity-[0.12]"
        src="/videos/hero-background.mp4"
        poster="/images/hero-poster.jpg"
      />
      <div className="absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_top_left,rgba(37,99,235,0.25),transparent_55%)]" />
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-gray-950/70 via-transparent to-gray-950" />

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">

          {/* ── LEFT: Copy + CTA ── */}
          <div className="lg:col-span-6 flex flex-col items-start text-left">
            {/* Eyebrow */}
            <span className="inline-flex items-center gap-2 bg-yellow-400/10 text-yellow-300 border border-yellow-400/30 text-xs font-bold uppercase tracking-[0.15em] px-4 py-1.5 rounded-full mb-6">
              <Sparkles className="h-4 w-4" />First 60 seconds free — no credit card
            </span>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-5">
              They played the game.
              <span className="block text-yellow-400">Now they get the podcast.</span>
            </h1>

            {/* Subhead — single, focused value prop */}
            <p className="text-lg text-gray-300/90 mb-8 max-w-xl leading-relaxed">
              Upload a GameChanger box score or snap a photo of the scoresheet. In minutes you get a
              shareable, pro-sounding recap with every player called out by name.
            </p>

            {/* Single, clear CTA */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-auto">
              <Link
                to="/create"
                className="group inline-flex items-center justify-center gap-2.5 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-black text-lg py-4 px-8 rounded-xl shadow-lg shadow-yellow-400/25 transition-all duration-200 hover:scale-[1.02]"
              >
                <span>Try It With Your Game</span>
                <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <span className="text-sm text-gray-400">
                <span className="text-yellow-300 font-bold">Free to start.</span> Pay only when you love it.
              </span>
            </div>

            {/* Compact trust row */}
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-400">
              {['No editing required', 'Real player shout-outs', 'Ready in minutes'].map((item) => (
                <span key={item} className="inline-flex items-center gap-2">
                  <Check className="h-4 w-4 text-electric-green" />{item}
                </span>
              ))}
            </div>
          </div>

          {/* ── RIGHT: Sample player ── */}
          <div className="lg:col-span-6">
            <div className="relative">
              {/* Soft glow behind the card */}
              <div className="absolute -inset-4 bg-gradient-to-tr from-royal-blue/30 to-yellow-400/20 blur-2xl rounded-[2rem]" aria-hidden="true" />

              <div className="relative bg-gray-900/70 backdrop-blur-md rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
                {/* Header strip */}
                <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/5">
                  <span className="inline-flex items-center gap-2 text-xs font-bold text-yellow-300 uppercase tracking-widest">
                    <span className="flex h-2 w-2 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400" />
                    </span>
                    Sample Podcast
                  </span>
                  <span className="text-xs font-semibold text-gray-500">Hit play</span>
                </div>

                <div className="p-5">
                  {/* Video */}
                  <div
                    className="relative rounded-2xl overflow-hidden bg-black cursor-pointer group"
                    onClick={togglePlayPause}
                  >
                    <video
                      ref={videoRef}
                      className="w-full h-auto max-h-[320px] object-contain"
                      onTimeUpdate={handleTimeUpdate}
                      onLoadedMetadata={handleLoadedMetadata}
                      onEnded={() => setIsPlaying(false)}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      poster="/images/hero-poster.jpg"
                    >
                      <source src="/videos/sample_video2.mp4" type="video/mp4" />
                    </video>

                    <div className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-200 ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
                      <span className="w-16 h-16 rounded-full bg-yellow-400 group-hover:bg-yellow-300 flex items-center justify-center shadow-xl transition-all duration-200 group-hover:scale-105">
                        {isPlaying
                          ? <Pause className="h-7 w-7 text-gray-900" />
                          : <Play className="h-7 w-7 text-gray-900 ml-1" />}
                      </span>
                    </div>
                  </div>

                  {/* Track info + scrubber */}
                  <div className="mt-4 space-y-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-black text-white text-sm truncate">"Thunder Hawks Walk Off in Extra Innings!" 🎙️</p>
                        <p className="text-xs text-gray-400 mt-0.5">Thunder Hawks vs Lightning Bolts · 12U Baseball</p>
                      </div>
                      <Volume2 className="h-4 w-4 text-gray-500 shrink-0" />
                    </div>

                    <div
                      className="relative w-full h-2 bg-white/10 rounded-full cursor-pointer group"
                      onClick={handleSeek}
                    >
                      <div
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-royal-blue to-yellow-400 rounded-full transition-all duration-100"
                        style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                      >
                        <div className="absolute right-0 -top-1 w-4 h-4 bg-white rounded-full translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity shadow-md" />
                      </div>
                    </div>

                    <div className="flex justify-between text-xs font-mono text-gray-500">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>
                </div>

                <div className="px-5 pb-5 pt-1 text-center border-t border-white/5">
                  <p className="text-xs text-gray-500 pt-3">
                    Imagine your kid's name in there.{' '}
                    <span className="text-yellow-400 font-semibold">Yours can sound just like this.</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
